import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, ArrowRight, Newspaper, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import type { BlogPost } from '../types';
import { formatDate } from '../lib/format';
import { Spinner, EmptyState } from '../components/ui';
import ImageHero from '../components/ImageHero';

const DESKTOP_PER_PAGE = 9;
const MOBILE_PER_PAGE = 4;

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    api.get<BlogPost[]>('/blog').then(setPosts).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const perPage = isMobile ? MOBILE_PER_PAGE : DESKTOP_PER_PAGE;
  const totalPages = Math.ceil(posts.length / perPage);
  const paged = posts.slice((page - 1) * perPage, page * perPage);

  return (
    <div>
      {/* Hero Section */}
      <ImageHero height="h-screen" imageKey="hero_image_properties" fallbackClass="bg-property-hero">
        <div className="max-w-7xl mx-auto px-4 h-full flex items-start pt-[33vh]">
          <div className="text-white max-w-3xl">
            <p className="uppercase tracking-[0.35em] text-primary text-xs md:text-sm font-semibold mb-4">News &amp; Insights</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-4 capitalize">
              Blog
            </h1>
            <p className="uppercase tracking-[0.35em] text-white text-xs md:text-sm font-semibold">
              Real Estate News, Market Research And Updates From Our Desk
            </p>
          </div>
        </div>
      </ImageHero>

      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <Spinner label="Loading posts..." />
          ) : posts.length === 0 ? (
            <EmptyState message="No blog posts published yet. Check back soon." />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
                {paged.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="block group">
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow h-full flex flex-col">
                      <div className="relative h-44 md:h-56 overflow-hidden bg-gray-100">
                        {post.cover_image && (
                          <img
                            src={post.cover_image}
                            alt={post.title}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 text-white">
                          <div className="flex items-center text-xs mb-2">
                            <span className="flex items-center">
                              <CalendarDays className="h-3.5 w-3.5 mr-1 text-primary" /> {formatDate(post.created_at)}
                            </span>
                          </div>
                          <h2 className="text-base md:text-xl font-semibold leading-snug line-clamp-2 capitalize">{post.title}</h2>
                        </div>
                      </div>
                      <div className="p-4 md:p-5 flex flex-col flex-1">
                        <p className="text-charcoal text-sm line-clamp-3 mb-4 flex-1">
                          {post.excerpt || post.content}
                        </p>
                        <span className="inline-flex items-center text-primary font-semibold text-sm group-hover:underline">
                          Read More
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-10">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Don't Miss An Update - full width band */}
        <div className="mt-16 bg-obsidian py-14 px-4 text-center relative overflow-hidden border-y border-primary/20">
          <Newspaper className="h-10 w-10 text-primary mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-2 capitalize">Don't Miss An Update</h2>
          <p className="text-taupe max-w-2xl mx-auto mb-6 leading-relaxed">
            Explore our properties and see what's happening across our managed estates.
          </p>
          <Link
            to="/sales"
            className="inline-flex items-center bg-primary text-black px-8 py-2 font-semibold hover:bg-primary-light transition-colors"
          >
            View Properties
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
